import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupplierAttributeType } from '@prisma/client';
import { Alert, Button, Card, Form, Input, Radio, Result, Space } from 'antd';
import { isAxiosError } from 'axios';
import { DefaultOptionType } from 'rc-select/lib/Select';

import { SupplierAttributeDetail } from '@app/domain-interfaces';
import { ConfirmDelete } from '@app/web-components';

import { useDeleteSupplierAttribute, useEditSupplierAttribute } from '../data';

type FormData = {
  supplier: DefaultOptionType;
  attribute: string;
  attributeType: SupplierAttributeType;
};

export type EditFormProps = {
  record: SupplierAttributeDetail;
};

export const EditForm = ({ record }: EditFormProps) => {
  const [deleteTarget, setDeleteTarget] = useState<SupplierAttributeDetail | undefined>(undefined);
  const del = useDeleteSupplierAttribute();
  const edit = useEditSupplierAttribute();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const initialValues: FormData = {
    supplier: { label: record.supplier?.name, value: record.supplierId },
    attribute: record.attribute,
    attributeType: record.attributeType,
  };

  const handleOnFinish = ({ supplier, attribute, attributeType }: FormData) =>
    edit.mutate({
      id: record.id,
      attribute: attribute,
      attributeType: attributeType,
    });

  if (edit.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully edited Supplier Attribute mapping"
        extra={[
          <Button type="primary" key="go to Supplier Attribute Mapping" onClick={() => navigate('../')}>
            Go To Supplier Attribute Mapping
          </Button>,
        ]}
      />
    );
  }

  return (
    <Space size="large" style={{ width: '100%' }} wrap>
      <ConfirmDelete.Modal
        itemId={deleteTarget?.id}
        itemName={deleteTarget?.supplier.name}
        onClose={() => setDeleteTarget(undefined)}
        onOk={(data) => del.mutate(data, { onSuccess: () => navigate('../') })}
      />
      <Form
        disabled={edit.isPending || edit.isSuccess}
        form={form}
        initialValues={initialValues}
        labelCol={{ span: 4 }}
        layout="vertical"
        style={{ width: '100%', minWidth: 1000 }}
        onFinish={handleOnFinish}
      >
        {edit.error ? (
          <Form.Item label=" " colon={false} hidden={!edit.error}>
            <Alert
              message="Error"
              description={isAxiosError(edit.error) ? edit?.error.response?.data['message'] : edit.error}
              type="error"
              showIcon
            />
          </Form.Item>
        ) : undefined}
        <Card>
          <Form.Item label="Supplier" name="supplier">
            <span>{initialValues.supplier.label}</span>
          </Form.Item>
          <Form.Item
            label="Attribute Type"
            name="attributeType"
            rules={[{ required: true, message: 'Attribute type is required.' }]}
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button value={SupplierAttributeType.DMS_NAME}>DMS</Radio.Button>
              <Radio.Button value={SupplierAttributeType.T3_NAME}>T3</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Attribute Name"
            name="attribute"
            style={{ paddingBottom: 12 }}
            hasFeedback
            rules={[
              { required: true, message: 'Attribute name is required.' },
              // { pattern: new RegExp(/^([A-Z0-9_-]+)$/), message: 'Invalid attribute name' },
            ]}
          >
            <Input />
          </Form.Item>
          <Space style={{ marginTop: 20 }}>
            <Button type="primary" htmlType="submit" loading={edit.isPending}>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button onClick={() => navigate('../')}>Cancel</Button>
          </Space>
        </Card>
      </Form>
    </Space>
  );
};
